import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ASSESS_STATUS_LABEL, OPP_LEVEL_LABEL, statusBadge, levelBadge, fmtDateTime } from "@/lib/adminMeta";
import { ArrowLeft, Flame, Mail, Phone, CheckCircle2, Clock, ExternalLink, Sparkles, Save } from "lucide-react";
import { toast } from "sonner";

const Row = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 py-2 border-b border-border last:border-0">
    <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold sm:w-72 shrink-0">{label}</div>
    <div className="text-sm text-navy break-words">{value || "-"}</div>
  </div>
);

export default function AdminAssessmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [internal, setInternal] = useState({ internal_notes: "", next_follow_up: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/assessments/${id}`).then(({ data }) => {
      setA(data);
      setInternal({ internal_notes: data.internal_notes || "", next_follow_up: (data.next_follow_up || "").slice(0, 10) });
    }).catch(() => setA(false));
  }, [id]);

  const saveInternal = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/assessments/${id}`, internal);
      setA((p) => ({ ...p, internal_notes: data.internal_notes, next_follow_up: data.next_follow_up }));
      setInternal({ internal_notes: data.internal_notes || "", next_follow_up: (data.next_follow_up || "").slice(0, 10) });
      toast.success("Notes saved");
    } catch { toast.error("Could not save notes"); } finally { setSaving(false); }
  };

  if (a === false) return <div className="text-muted-foreground">Assessment not found.</div>;
  if (!a) return <div className="text-muted-foreground">Loading...</div>;
  const c = a.contact;

  return (
    <div className="space-y-6 fade-up max-w-5xl" data-testid="admin-assessment-detail">
      <button onClick={() => navigate("/admin/assessments")} className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-navy" data-testid="assess-detail-back">
        <ArrowLeft className="w-4 h-4" /> Back to Assessments
      </button>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-bold text-navy">{a.identified ? (c?.name || c?.email || "Identified Prospect") : "Anonymous Assessment"}</h1>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${statusBadge(a.status)}`}>{ASSESS_STATUS_LABEL[a.status] || a.status}</span>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${levelBadge(a.top_level)}`}>{OPP_LEVEL_LABEL[a.top_level] || a.top_level_label}</span>
      </div>

      {a.high_priority && (
        <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-5" data-testid="assess-priority-banner">
          <div className="flex items-center gap-2 text-red-700 font-bold"><Flame className="w-5 h-5" /> High-Priority Prospect</div>
          <p className="text-sm text-red-700/80 mt-1">Identified prospect with a High or Very High Stock-to-Sale Attribution opportunity. Supporting signals:</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {(a.signals || []).map((s) => (
              <span key={s.key} className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${s.primary ? "bg-red-600 text-white border-red-600" : "bg-white text-red-700 border-red-200"}`} data-testid={`assess-signal-${s.key}`}>{s.label}</span>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Contact / prospect */}
        <div className="bg-white rounded-2xl border border-border p-6 h-fit" data-testid="assess-contact-card">
          <h2 className="text-lg font-bold text-navy mb-3">Prospect</h2>
          {a.identified ? (
            <>
              <div className="flex flex-wrap gap-2 mb-3">
                {c?.email && <a href={`mailto:${c.email}`} data-testid="assess-quick-email"><Button variant="outline" size="sm"><Mail className="w-4 h-4 mr-1.5" /> Email</Button></a>}
                {c?.phone && <a href={`tel:${c.phone}`} data-testid="assess-quick-call"><Button variant="outline" size="sm"><Phone className="w-4 h-4 mr-1.5" /> Call</Button></a>}
              </div>
              <Row label="First Name" value={c?.first_name} />
              <Row label="Last Name" value={c?.last_name} />
              <Row label="Dealership Name" value={c?.dealership_name} />
              <Row label="Email" value={c?.email} />
              <Row label="Phone" value={c?.phone} />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">This visitor completed the assessment anonymously. No contact information has been provided. Only non-identifying assessment data is shown.</p>
          )}
          <Row label="Assessment ID" value={a.id} />
          <Row label="Assessment Date" value={fmtDateTime(a.created_at)} />

          <div className="mt-4 space-y-2">
            <div className={`flex items-center gap-2 text-sm ${a.email_requested ? "text-emerald-700" : "text-muted-foreground"}`} data-testid="assess-status-emailed">
              {a.email_requested ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
              Assessment Emailed {a.email_requested && a.email_requested_at ? `(${fmtDateTime(a.email_requested_at)})` : ""}
            </div>
            <div className={`flex items-center gap-2 text-sm ${a.demo ? "text-emerald-700" : "text-muted-foreground"}`} data-testid="assess-status-demo">
              {a.demo ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
              {a.status === "demo_scheduled" ? "Demo Scheduled" : "Demo Requested"}
              {a.demo && (
                <button onClick={() => navigate(`/admin/demo-requests/${a.demo.id}`)} className="inline-flex items-center gap-1 text-navy font-semibold hover:underline" data-testid="assess-demo-link">
                  View <ExternalLink className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Opportunity levels */}
        <div className="bg-white rounded-2xl border border-border p-6 h-fit" data-testid="assess-opportunities-card">
          <h2 className="text-lg font-bold text-navy mb-3">Opportunity Levels</h2>
          <div className="space-y-2">
            {(a.categories || []).map((cat) => (
              <div key={cat.key} className="flex items-center justify-between gap-3">
                <span className="text-sm text-navy">{cat.label}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${levelBadge(cat.level)}`}>{cat.level_label}</span>
              </div>
            ))}
          </div>
          <h3 className="text-sm font-bold text-navy mt-5 mb-2">Recommended Marine Ad AI Capabilities</h3>
          <div className="space-y-2">
            {(a.recommended_capabilities || []).map((cap) => (
              <div key={cap.key} className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-navy">{cap.label}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${cap.available ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {cap.available ? "Available" : "Coming Soon"}
                  </span>
                </div>
                {cap.blurb && <p className="text-xs text-muted-foreground mt-1">{cap.blurb}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {a.identified && (
        <div className="bg-white rounded-2xl border border-border p-6" data-testid="assess-notes-card">
          <h2 className="text-lg font-bold text-navy mb-4">Internal (Admin Only)</h2>
          <div className="grid gap-4 sm:grid-cols-[220px_1fr] sm:items-start">
            <div className="space-y-1.5">
              <Label>Next Follow-Up Date</Label>
              <Input type="date" value={internal.next_follow_up} onChange={(e) => setInternal((s) => ({ ...s, next_follow_up: e.target.value }))} data-testid="assess-followup" />
            </div>
            <div className="space-y-1.5">
              <Label>Internal Notes</Label>
              <Textarea rows={4} value={internal.internal_notes} onChange={(e) => setInternal((s) => ({ ...s, internal_notes: e.target.value }))} placeholder="Private notes visible only to owner and admins" data-testid="assess-notes" />
            </div>
          </div>
          <Button onClick={saveInternal} disabled={saving} className="mt-4 bg-navy hover:bg-navy-light text-white font-bold" data-testid="assess-notes-save">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Notes"}
          </Button>
        </div>
      )}

      {/* AI assessment */}
      {a.ai_assessment && (
        <div className="bg-white rounded-2xl border border-border p-6" data-testid="assess-ai-card">
          <h2 className="text-lg font-bold text-navy mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-gold" /> AI-Generated Assessment</h2>
          <p className="text-sm text-navy/90 leading-relaxed whitespace-pre-line">{a.ai_assessment}</p>
        </div>
      )}

      {/* Dealership profile / answers */}
      <div className="bg-white rounded-2xl border border-border p-6" data-testid="assess-profile-card">
        <h2 className="text-lg font-bold text-navy mb-3">Dealership Profile</h2>
        {(a.answers_labeled || []).map((qa) => (
          <Row key={qa.id} label={qa.question} value={qa.answer} />
        ))}
      </div>
    </div>
  );
}
